import React, {useContext} from 'react';
import {
  present,
  blank,
  numberFormat,
  maybe,
  componentJoin,
  mapWithIndex,
} from '@/util';
import Icon from '@/views/shared/Icon';
import {Card, CardBody, Row, Col, Badge} from 'reactstrap';
import {RevenueCtx} from '@/contexts';
import AmoebaLink from '@/components/AmoebaLink';
import * as R from 'ramda';

const Detail = () => {
  const data = useContext(RevenueCtx);
  if (blank(data)) {
    return null;
  }

  // FIXME: need to refactor
  const contributionAmountElement = (
    isFloat,
    contributionAmount,
    combinedContributionAmount,
    productRecordType
  ) =>
    maybe(isFloat)
      .map(() => numberFormat.toDollar(contributionAmount))
      .orElse(
        <>
          {contributionAmount}
          {maybe(
            productRecordType !== 'IL' && present(combinedContributionAmount)
          ).map(() => (
            <React.Fragment key='combinedContributionAmount'>
              <br />
              <Badge color='success'>
                Total: {numberFormat.toDollar(combinedContributionAmount)}
              </Badge>
            </React.Fragment>
          ))}
        </>
      );

  const contributionAmountDiffElement = (
    contributionAmount,
    originalContributionAmount
  ) => {
    if (contributionAmount !== originalContributionAmount) {
      return maybe(present(originalContributionAmount)).map(() => (
        <Badge color='info'>Total: {originalContributionAmount}</Badge>
      ));
    }

    return null;
  };

  const commissionColumnName = R.cond([
    [R.equals('R'), R.always('Premium Amount')],
    [R.equals('A'), R.always('AUM Amount')],
    [R.equals('T'), R.always('AUM Amount')],
    [R.T, R.always('Contribution Amount')],
  ]);

  const prepareClient = R.compose(
    componentJoin(' / '),
    mapWithIndex((item, idx) => (
      <AmoebaLink key={`link_${idx}`} href={`/clients/${item.id}`}>
        {item.name}
      </AmoebaLink>
    ))
  );

  return (
    <Card>
      <CardBody>
        <Col md={12}>
          <fieldset className='well well-small compact' id='revenue-details'>
            <legend>Revenue Details</legend>
            <Row form>
              <Col md={2}>
                <strong>Commission Type</strong>
              </Col>
              <Col md={3}>
                <strong>Received Curr. &amp; Ex. Rate</strong>
              </Col>
              <Col md={2}>
                <strong>Service Company</strong>
              </Col>
              <Col md={3}>
                <strong>Pay-in Amount</strong>
              </Col>
              <Col md={2}>
                <strong>Ref. No.</strong>
              </Col>
            </Row>
            <Row form>
              <Col md={2}>{data.commissionType}</Col>
              <Col md={3}>
                {data.receivedCurrency} (
                {numberFormat.toDecimal(data.providerExchangeRate)})
              </Col>
              <Col md={2}>
                <AmoebaLink href={`/producers/${data.serviceCompanyId}`}>
                  {data.serviceCompany}
                </AmoebaLink>
              </Col>
              <Col md={3}>
                {numberFormat.toDollar(data.totalPayinAmount)}
                <br />
                (Effective on: {data.payinEffectiveDate})
              </Col>
              <Col md={2}>{data.refNo}</Col>
            </Row>
            <Row form>
              <Col md={2}>
                <strong>Policy No.</strong>
              </Col>
              <Col md={3}>
                <strong>
                  {maybe(data.commissionType === 'C')
                    .map(() => 'Lapse Surrender Date')
                    .orElse('Premium Date')}
                  Premium Date
                </strong>
              </Col>
              <Col md={2}>
                <strong>Pay-in Mode</strong>
              </Col>
              <Col md={3}>
                <strong>Pay-out Amount</strong>
              </Col>
              <Col md={2}>
                <strong>Split Case</strong>
              </Col>
            </Row>
            <Row form>
              <Col md={2}>
                <AmoebaLink
                  href={`/policies/${R.pathOr('', ['policy', 'id'], data)}`}
                >
                  {data.accountNo}
                </AmoebaLink>
              </Col>
              <Col md={3}>
                {maybe(data.commissionType === 'C')
                  .map(() => data.lapseSurrenderOn)
                  .orElse(data.premiumDate)}
              </Col>
              <Col md={2}>{data.payinMode}</Col>
              <Col md={3}>
                {numberFormat.toDollar(data.totalPayoutAmount)}
                <br />
                (Effective on: {data.payoutEffectiveDate})
              </Col>
              <Col md={2}>
                {maybe(present(data.join_seq_no))
                  .map(() => <Icon icon='check' />)
                  .orElse(<Icon icon='times' />)}
              </Col>
            </Row>
            <Row form>
              <Col md={2}>
                <strong>Revenue Start Month</strong>
              </Col>
              <Col md={3}>
                <strong>Bank-in Date</strong>
              </Col>
              <Col md={2}>
                <strong />
              </Col>
              <Col md={2}>
                <strong>Gross Profit</strong>
              </Col>
              <Col md={{size: 2, offset: 1}}>
                <strong>Upfront Fee</strong>
              </Col>
            </Row>
            <Row form>
              <Col md={2}>{data.cutoffOn}</Col>
              <Col md={3}>{data.bankInOn}</Col>
              <Col md={2} />
              <Col md={2}>
                {numberFormat.toDollar(data.grossProfit)} (
                {numberFormat.toPercentage(data.grossProfitPercentage)})
              </Col>
              <Col md={{size: 2, offset: 1}}>{data.upfrontWrapFee}</Col>
            </Row>
            <Row form>
              <Col md={5}>
                <strong>Producer</strong>
              </Col>
              <Col md={2}>
                <strong />
              </Col>
              <Col md={2}>
                <strong>{commissionColumnName(data.commissionType)}</strong>
              </Col>
              {/* FIXME: cover back bv factor */}
            </Row>
            <Row form>
              <Col md={5}>
                <AmoebaLink href={`/producers/${data.serviceCompanyId}`}>
                  {data.serviceCompany}
                </AmoebaLink>
              </Col>
              <Col md={2} />
              <Col md={2}>
                {contributionAmountElement(
                  data.isContributionAmountFloat,
                  data.contributionAmount,
                  data.combinedContributionAmount,
                  data.productRecordType
                )}
                {contributionAmountDiffElement(
                  data.contributionAmount,
                  data.originalContributionAmount
                )}
                {/* FIXME: cover back bv factor */}
              </Col>
            </Row>
            <Row form>
              <Col md={10}>
                <strong>Plan</strong>
              </Col>
              <Col md={2}>
                <strong>Orphan Case</strong>
              </Col>
            </Row>
            <Row form>
              <Col md={10}>
                <AmoebaLink href={`/products/${data.productId}`}>
                  {data.productName}
                </AmoebaLink>{' '}
                ({R.pathOr('', ['policyItem', 'type'], data)}) (Commence On:{' '}
                {R.pathOr('', ['policyItem', 'commenceOn'], data)})
              </Col>
              <Col md={2}>
                {maybe(data.isOrphanCase === true)
                  .map(() => <Icon icon='check' />)
                  .orElse(<Icon icon='times' />)}
              </Col>
            </Row>
            <Row form>
              <Col md={10}>
                <strong>Plan Details</strong>
              </Col>
              <Col md={2}>
                <strong>Sales Campaign</strong>
              </Col>
            </Row>
            <Row form>
              <Col md={10}>
                <Badge color='secondary'>Plan</Badge>
                &nbsp;
                <AmoebaLink href={`/products/${data.productId}`}>
                  {data.productName}
                </AmoebaLink>
                &nbsp;
                <Badge color='secondary'>Premium</Badge>
                &nbsp;
                {R.pathOr('', ['policy', 'currency'], data)}
                &nbsp;
                {numberFormat.toDollar(
                  R.pathOr('', ['policyItem', 'modalPremium'], data)
                )}{' '}
                / {data.payMode}
                &nbsp;
                <Badge color='secondary'>Term</Badge>
                &nbsp;
                {data.term}
                &nbsp;
                <Badge color='secondary'>Client</Badge>
                &nbsp;
                {prepareClient(R.propOr([], 'client', data))}
              </Col>
              <Col md={2}>{data.salesCampaign}</Col>
            </Row>
          </fieldset>
        </Col>
      </CardBody>
    </Card>
  );
};

export default Detail;
